import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchEvents, buildAssetUrl, friendlyError } from '@/lib/api';
import { ROUTES } from '@/lib/constants';
import { formatEventDate } from '@/components/events/EventStatusBadge';
import { toast } from 'sonner';

// Imagen hero suministrada por el cliente — contiene branding y claim ya
// "horneados" en la propia creatividad. Encima sólo se superponen
// elementos funcionales (botones y enlaces reales).
const HERO_IMAGE_URL =
  'https://customer-assets-7cd3h4nn.emergentagent.net/job_scout-arch/artifacts/2yuo34f1_Banner%20deportivo%20futurista%20con%20anal%C3%ADtica%20multideporte.webp';

// --- Carrusel: últimos 5 partidos ---------------------------------------

function MatchCard({ event }) {
  const cover = buildAssetUrl(event.thumbnail_url);
  const teamsLine =
    [event.team_home?.name, event.team_away?.name].filter(Boolean).join(' vs ') ||
    event.title ||
    'Partido';
  const dateLine = formatEventDate(event.event_date || event.scheduled_at);
  return (
    <Link
      to={ROUTES.eventDetail(event.slug)}
      data-testid={`home-match-card-${event.slug}`}
      className="group relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#141014] hover:border-[var(--ns-gold)]/50 transition-all duration-200"
    >
      <div className="relative w-full aspect-[16/9] bg-gradient-to-br from-[var(--ns-gold)]/20 to-black overflow-hidden">
        {cover ? (
          <img
            src={cover}
            alt={teamsLine}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#2a1e12] to-[#0d0a08]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        {event.sport_name ? (
          <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-white bg-[var(--ns-gold)]/90 text-black px-2 py-1 rounded-full">
            {event.sport_name}
          </div>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3
          className="font-display text-white text-base leading-tight line-clamp-2 min-h-[2.6em]"
          data-testid={`home-match-title-${event.slug}`}
        >
          {teamsLine}
        </h3>
        <p className="mt-1.5 text-[11px] text-white/60 font-mono line-clamp-1">{dateLine}</p>
        <div className="mt-auto pt-3 inline-flex items-center gap-1 text-sm text-[var(--ns-gold)] group-hover:gap-2 transition-all">
          Ver partido <ArrowRight className="h-3.5 w-3.5" />
        </div>
      </div>
    </Link>
  );
}

function LatestMatchesCarousel() {
  const [items, setItems] = useState(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchEvents({ page: 1, page_size: 5 });
        const list = res?.items || [];
        setItems(list.slice(0, 5));
      } catch (err) {
        setItems([]);
        toast.error(friendlyError(err));
      }
    })();
  }, []);

  const scrollByCards = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    const firstItem = el.querySelector('[data-carousel-item]');
    const gap = window.matchMedia('(min-width: 1024px)').matches ? 24 : 16;
    const step = firstItem ? firstItem.clientWidth + gap : 320;
    el.scrollBy({ left: step * direction, behavior: 'smooth' });
  };

  const showEmpty = items && items.length === 0;
  const showLoading = items === null;

  return (
    <section
      className="w-full bg-[#0b0a09] border-t border-white/5"
      data-testid="home-latest-matches"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-5 lg:pt-6 pb-8 sm:pb-10">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className="font-display text-white text-xl sm:text-2xl tracking-wide uppercase">
            Últimos partidos
          </h2>
          {items && items.length > 1 ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => scrollByCards(-1)}
                aria-label="Desplazar a la izquierda"
                className="h-10 w-10 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/40 hover:bg-white/5 transition flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ns-gold)]/50"
                data-testid="home-carousel-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollByCards(1)}
                aria-label="Desplazar a la derecha"
                className="h-10 w-10 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/40 hover:bg-white/5 transition flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ns-gold)]/50"
                data-testid="home-carousel-next"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          ) : null}
        </div>

        {showLoading ? (
          <div className="flex gap-4 lg:gap-6 overflow-hidden">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="shrink-0 w-[85%] sm:w-[calc(50%-0.5rem)] lg:w-[calc((100%-3rem)/3)]"
              >
                <Skeleton className="w-full aspect-[16/9] rounded-2xl bg-white/5" />
                <Skeleton className="mt-3 h-4 w-3/4 bg-white/5" />
                <Skeleton className="mt-2 h-3 w-1/3 bg-white/5" />
              </div>
            ))}
          </div>
        ) : showEmpty ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-10 text-center text-white/60 text-sm">
            Aún no hay partidos publicados.
          </div>
        ) : (
          <div
            ref={scrollerRef}
            className="flex gap-4 lg:gap-6 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-none"
            style={{ scrollbarWidth: 'none' }}
            data-testid="home-carousel-scroller"
          >
            {items.map((ev) => (
              <div
                key={ev.id || ev.slug}
                data-carousel-item
                className="snap-start shrink-0 w-[85%] sm:w-[calc(50%-0.5rem)] lg:w-[calc((100%-3rem)/3)] flex"
              >
                <MatchCard event={ev} />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// --- Hero limpio sin overlays HTML ---------------------------------------
// La imagen hero ya contiene todo el mensaje visual (marca, claim, iconos
// de categorías). No se superpone ningún CTA ni texto HTML encima: la
// navegación real vive en el header global.

function HeroFromAsset() {
  return (
    <section
      className="relative w-full bg-black overflow-hidden"
      data-testid="home-hero"
    >
      {/*
        Responsive mobile:
        - En móvil (<640px) usamos la proporción nativa de la creatividad
          (aspect-[2000/766]) para que la imagen se vea COMPLETA, sin
          recortar los jugadores laterales ni el claim central. Resultado:
          hero más compacto en móvil y sin cortes de branding.
        - En sm+ mantenemos el comportamiento desktop original con altura
          basada en viewport.
      */}
      <div className="relative w-full aspect-[2000/766] sm:aspect-auto sm:h-[62vh] md:h-[66vh] lg:h-[64vh] sm:min-h-[520px] sm:max-h-[760px]">
        <img
          src={HERO_IMAGE_URL}
          alt="NetScouters — Donde el deporte se ve, se entiende y evoluciona."
          className="absolute inset-0 w-full h-full object-cover sm:object-center select-none"
          draggable={false}
          data-testid="home-hero-image"
        />
        {/* Sombra inferior sutil para transición al carrusel oscuro.
            Sin CTAs ni textos HTML superpuestos: la imagen contiene ya
            todo el mensaje visual (marca, claim, iconos de categorías). */}
        <div className="absolute bottom-0 left-0 right-0 h-[18%] bg-gradient-to-t from-black via-black/50 to-transparent pointer-events-none" />
      </div>
    </section>
  );
}

// --- Página -------------------------------------------------------------

export default function Landing() {
  // Fondo global oscuro mientras esta home esté activa. Sobrescribe además el
  // fondo cream de MainLayout mediante body::before + selectores locales.
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#000';
    document.body.classList.add('ns-home-dark');
    return () => {
      document.body.style.backgroundColor = prev;
      document.body.classList.remove('ns-home-dark');
    };
  }, []);

  return (
    <MainLayout transparentHeader>
      {/* Forzar contenedores intermedios de MainLayout a fondo negro para
          eliminar cualquier franja cream visible detrás del hero. */}
      <style>{`
        body.ns-home-dark,
        body.ns-home-dark .min-h-screen,
        body.ns-home-dark #root,
        body.ns-home-dark #root > div,
        body.ns-home-dark #root > div > div,
        body.ns-home-dark main {
          background-color: #000 !important;
        }
      `}</style>
      <div className="bg-black min-h-[100vh]">
        <HeroFromAsset />
        <LatestMatchesCarousel />
      </div>
    </MainLayout>
  );
}
